import { useMemo } from 'react';
import useStorefrontTheme from './useStorefrontTheme';

/**
 * Storefront dark-mode neutral palette.
 *
 * Marketplace theme-section components (HeroBanner.minimal,
 * Newsletter, TrustBadges, etc.) historically hardcoded neutral
 * surfaces like `#ffffff` background + `#111827` text. Those
 * sections looked broken once a customer toggled the storefront
 * dark mode — the merchant's brand colors flipped via the
 * `[data-storefront-theme]` cascade, but the neutrals stayed
 * stuck at light values.
 *
 * This hook returns the neutral palette that matches the active
 * storefront theme (light or dark). Components consume it for
 * the surfaces that should flip — `background`, `text`,
 * `textMuted`, `border`, and so on. Brand colors (primary,
 * accent) stay in the per-section settings unchanged — those
 * are the merchant's identity and never auto-flip.
 *
 * Returned colors are kept in sync with the `[data-storefront-theme]`
 * CSS-variable values in `styles/storefrontTheme.css`. If those
 * tokens change, mirror the updates here.
 */

const LIGHT_NEUTRALS = {
  background:    '#ffffff',
  surface:       '#ffffff',
  surfaceAlt:    '#f9fafb',
  text:          '#111827',
  textMuted:     '#6b7280',
  textInverted:  '#ffffff',
  border:        '#e5e7eb',
  borderStrong:  '#d1d5db',
  shadow:        '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
};

const DARK_NEUTRALS = {
  background:    '#0b0f17',
  surface:       '#131825',
  surfaceAlt:    '#1a2030',
  text:          '#f3f4f6',
  textMuted:     '#9ca3af',
  textInverted:  '#0b0f17',
  border:        '#1f2937',
  borderStrong:  '#374151',
  shadow:        '0 1px 3px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
};

export const useDarkModeNeutrals = () => {
  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  return useMemo(
    () => (isDark ? DARK_NEUTRALS : LIGHT_NEUTRALS),
    [isDark]
  );
};

export { LIGHT_NEUTRALS, DARK_NEUTRALS };
export default useDarkModeNeutrals;
